import { useState } from 'react';

/**
 * BulkActionBar — floating action bar for multi-select workflows.
 *
 * Reuses each page's existing single-item action logic — the parent passes an
 * `onAction(actionKey, comment)` handler that just loops the same API call it
 * already uses for single rows. This component owns UI (comment input, buttons,
 * selection count, error reporting); it never talks to the API directly.
 *
 * Props:
 *  - selectedCount: number
 *  - actions: [{ key, label, className, requireConfirm? }]
 *  - onAction: (actionKey, comment) => Promise<{ ok, failed }>  // parent runs the loop
 *  - onClear: () => void   // clears selection
 *  - defaultComment?: string
 *  - placeholder?: string
 */
export default function BulkActionBar({
  selectedCount,
  actions,
  onAction,
  onClear,
  defaultComment = '',
  placeholder = 'Add a comment (applied to all selected)…',
}) {
  const [comment, setComment] = useState(defaultComment);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (selectedCount === 0) return null;

  const handleClick = async (actionKey) => {
    if (running) return;
    setRunning(true);
    setLastResult(null);
    try {
      const res = await onAction(actionKey, comment);
      setLastResult(res);
      if (res && res.failed === 0) {
        setComment('');
      }
    } catch (e) {
      setLastResult({ ok: 0, failed: selectedCount, error: e?.message || 'Unknown error' });
    }
    setRunning(false);
  };

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-[#E4E4E7] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3"
      data-testid="bulk-action-bar"
      style={{ minWidth: 640, maxWidth: '90vw' }}
    >
      <span className="text-sm font-medium text-[#0A0A0A] whitespace-nowrap" data-testid="bulk-selected-count">
        {selectedCount} selected
      </span>
      <input
        type="text"
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-3 py-1.5 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0052FF]"
        data-testid="bulk-comment-input"
        disabled={running}
      />
      <div className="flex gap-1.5 shrink-0">
        {actions.map(a => (
          <button
            key={a.key}
            type="button"
            onClick={() => handleClick(a.key)}
            disabled={running}
            data-testid={`bulk-action-${a.key}`}
            className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${a.className || 'bg-[#0052FF] text-white hover:bg-[#0040CC]'}`}
          >
            {a.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onClear}
          disabled={running}
          className="px-2.5 py-1.5 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-50"
          data-testid="bulk-clear-selection"
        >
          Clear
        </button>
      </div>

      {running && (
        <span className="text-xs text-[#A1A1AA] whitespace-nowrap">Applying…</span>
      )}
      {lastResult && !running && (
        <span
          className={`text-xs whitespace-nowrap ${lastResult.failed > 0 ? 'text-orange-600' : 'text-green-600'}`}
          data-testid="bulk-action-result"
        >
          {lastResult.failed > 0
            ? `${lastResult.ok} succeeded, ${lastResult.failed} failed`
            : `All ${lastResult.ok} succeeded`}
        </span>
      )}
    </div>
  );
}
